import { FunctionComponent, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';

import {
  BRANDS_DEFAULT_ENTRY,
  DASHBOARD_PATH,
  MY_CAMPAIGNS_HU_DEFAULT_ENTRY,
} from '@/constants/paths';
import { useAuthContext } from '@/contexts';
import { Loader } from '@/shared_elements';
import { CspProfile, User } from '@/types';
import { toastFlashMessage } from '@/utils';
import { globalGetService } from '@/utils/globalApiServices';
import { clearLocalStorage } from '@/utils/storage';

const Home: FunctionComponent = () => {
  const { setUser, setProfile } = useAuthContext();
  const { oktaAuth, authState } = useOktaAuth();
  const history = useHistory();
  const location = useLocation();

  const logout = () => {
    clearLocalStorage();
    oktaAuth.signOut();
  };

  const handleTokenExpired = () => {
    toastFlashMessage('Token expired! please try logging in again', 'error');
    logout();
  };

  const handleUnexpectedError = () => {
    toastFlashMessage('Please try logging in again', 'error');
    logout();
  };

  useEffect(() => {
    (async () => {
      if (!authState || authState.isLoading) {
        return;
      }
      if (!authState.isAuthenticated) {
        history.push('/login');
        return;
      }

      const userResponse = await globalGetService('user');
      if (userResponse.ok) {
        const user = userResponse.data as User;
        if (user) {
          setUser(user);
        }
      } else if (userResponse.status === 401) {
        handleTokenExpired();
        return;
      } else {
        handleUnexpectedError();
        return;
      }

      const profileResponse = await globalGetService('csp/profile');
      if (profileResponse.ok) {
        const profile = profileResponse.data as CspProfile;
        if (profile) {
          setProfile(profile);
          if (profile.status.toLowerCase() !== 'active') {
            history.push('/user/csp/status');
            return;
          }
        }
      } else if (profileResponse.status === 404) {
        history.push('/user/csp');
        return;
      } else if (profileResponse.status === 401) {
        handleTokenExpired();
        return;
      } else {
        handleUnexpectedError();
        return;
      }

      const params = new URLSearchParams(location.search);
      const redirectUri = params.get('redirectUri');
      if (redirectUri) {
        window.location.href = decodeURIComponent(redirectUri);
      } else {
        // TCR-20705: use profile from API response since auth context is not yet updated
        const profile = profileResponse.data as CspProfile;
        history.push(
          profile?.market === 'HU'
            ? MY_CAMPAIGNS_HU_DEFAULT_ENTRY
            : profile?.market === 'DE'
            ? BRANDS_DEFAULT_ENTRY
            : DASHBOARD_PATH
        );
      }
    })();
  }, [authState?.isLoading, authState?.isAuthenticated]);

  return <Loader hocFlag />;
};

export default Home;
